import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import Modal from 'react-modal';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../dashboardContent/config';
import { getFirstBaccalaureateOptions } from '../../constants/firstBaccalaureateOptions';
import '../../StylesCSS/DeuxiemeLangue.css';
import BranchLabelBanner from './BranchLabelBanner';

const successModalStyles = {
  overlay: {
    backgroundColor: 'rgba(24, 39, 55, 0.55)',
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(90vw, 430px)',
    padding: '2rem',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 18px 48px rgba(0, 0, 0, 0.25)',
  },
};

const DecisionConseilSelonDepartement = () => {
  const navigate = useNavigate();
  const { branch, branchLabel, action } = useLocation().state || {};
  const decisions = getFirstBaccalaureateOptions(branchLabel);
  const { userId } = useUser();
  const [institutions, setInstitutions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDecision, setSelectedDecision] = useState('');
  const [seuil, setSeuil] = useState(null);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/version2/api/excel/institutions/${userId}`, {
      params: { branch: branch || '', branchLabel: branchLabel || '' },
    }).then((response) => setInstitutions(response.data))
      .catch((error) => console.error('Erreur lors de la récupération des institutions:', error));
  }, [userId, branch, branchLabel]);

  useEffect(() => {
    if (!selectedInstitution) {
      setClasses([]);
      setSelectedClass('');
      return;
    }

    axios.get(`${API_BASE_URL}/version2/api/excel/classes/${userId}/${encodeURIComponent(selectedInstitution)}`, {
      params: { branch: branch || '', branchLabel: branchLabel || '' },
    }).then((response) => setClasses(response.data))
      .catch((error) => console.error('Erreur lors de la récupération des classes:', error));
  }, [userId, selectedInstitution, branch, branchLabel]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/version2/api/seuil/${userId}`, {
      params: { branchLabel: branchLabel || '' },
    }).then((response) => setSeuil(Number(response.data.seuil)))
      .catch((error) => console.error('Erreur lors de la récupération du seuil:', error));
  }, [userId, branchLabel]);

  const handleSave = async () => {
    if (!selectedInstitution || !selectedClass || !selectedDecision || seuil === null) return;

    try {
      const response = await axios.put(`${API_BASE_URL}/version2/api/students/update-decision`, null, {
        params: { userId, institution: selectedInstitution, classe: selectedClass, decision: selectedDecision, seuil, branchLabel: branchLabel || '' },
      });
      setUpdatedCount(response.data.updatedCount);
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la décision:', error);
    }
  };

  return (
    <Box
      component="form"
      className="second-language-page"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const requiredFieldIds = ['institution', 'class', 'decision'];
        const firstEmptyField = requiredFieldIds
          .map((fieldId) => event.currentTarget.elements.namedItem(fieldId))
          .find((field) => field && !field.value);

        if (firstEmptyField) {
          firstEmptyField.reportValidity();
          return;
        }

        handleSave();
      }}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '2rem', backgroundColor: '#f4f6f8', border: '1px solid #dcdcdc', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', maxWidth: '400px', margin: 'auto', textAlign: 'right' }}
    >
      <BranchLabelBanner branchLabel={branchLabel} />
      <div className="second-language-heading">
        <Typography variant="h5" sx={{ fontWeight: '500', color: '#2c3e50', textAlign: 'center' }}>اختيار قرار المجلس حسب القسم</Typography>
      </div>
      <FormControl className="second-language-field" fullWidth sx={{ width: '300px' }}>
        <InputLabel>اختر المؤسسة</InputLabel>
        <Select id="institution" name="institution" required value={selectedInstitution} label="اختر المؤسسة" onChange={(event) => setSelectedInstitution(event.target.value)} sx={{ direction: 'rtl', backgroundColor: '#ffffff' }}>
          {institutions.map((institution) => <MenuItem key={institution} value={institution}>{institution}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl className="second-language-field" fullWidth disabled={!selectedInstitution} sx={{ width: '300px' }}>
        <InputLabel>رقم القسم</InputLabel>
        <Select id="class" name="class" required value={selectedClass} label="رقم القسم" onChange={(event) => setSelectedClass(event.target.value)} sx={{ direction: 'rtl', backgroundColor: '#ffffff' }}>
          {classes.map((classe) => <MenuItem key={classe} value={classe}>{classe}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl className="second-language-field" fullWidth disabled={!selectedClass} sx={{ width: '300px' }}>
        <InputLabel>قرار المجلس</InputLabel>
        <Select id="decision" name="decision" required value={selectedDecision} label="قرار المجلس" onChange={(event) => setSelectedDecision(event.target.value)} sx={{ direction: 'rtl', backgroundColor: '#ffffff' }}>
          <MenuItem value="">-- اختر --</MenuItem>
          {decisions.map((decision) => <MenuItem key={decision} value={decision}>{decision}</MenuItem>)}
        </Select>
      </FormControl>
      <Box className="second-language-actions" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '1rem' }}>
        <Button type="button" variant="outlined" onClick={() => navigate('/PageLayout2V2', { state: { branch, branchLabel, action } })} sx={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c', color: '#ffffff', '&:hover': { backgroundColor: '#c0392b' } }}>رجوع</Button>
        <Button type="submit" variant="contained" disabled={!selectedInstitution || !selectedClass || !selectedDecision || seuil === null} sx={{ backgroundColor: '#087f83', color: '#ffffff', '&:hover': { backgroundColor: '#056568' } }}>تاكيد</Button>
      </Box>
      <Modal
        isOpen={isSuccessModalOpen}
        onRequestClose={() => setIsSuccessModalOpen(false)}
        contentLabel="تأكيد اختيار قرار المجلس"
        style={successModalStyles}
      >
        <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'grid', placeItems: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#e8f7ee', color: '#198754', fontSize: '2rem', fontWeight: 700 }}>✓</Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238' }}>لقد تم اختيار قرار المجلس حسب القسم بنجاح</Typography>
          <Typography variant="body2" sx={{ color: '#5f6b73' }}>تم تطبيق القرار على {updatedCount} تلميذ مستوفٍ لعتبة النجاح.</Typography>
          <Button variant="contained" onClick={() => setIsSuccessModalOpen(false)} sx={{ minWidth: 120, backgroundColor: '#27ae60' }}>Fermer</Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default DecisionConseilSelonDepartement;